/**
 * Dashboard banner that surfaces auto-imported transactions from the
 * overnight Gmail background sync.
 *
 * Phase 15 contract:
 *   • Backend GET /api/gmail/auto-imports/banner returns
 *     { show, auto_applied, drafts_created, last_auto_sync_at }.
 *   • Banner renders ONLY when `show=true` (server-side dismiss persists
 *     across refreshes — no client-side localStorage flicker).
 *   • Two primary actions:
 *       – "Review" → /transactions?source=gmail-auto (lands the user on the
 *         filtered list).
 *       – "Dismiss" → POST /api/gmail/auto-imports/banner/dismiss.
 *
 * We deliberately render NOTHING (no skeleton, no spacer) during the loading
 * window — most users won't have anything to surface and we don't want a
 * dashboard layout shift on every page load.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, MailCheck, X, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Gmail as GmailApi } from '@/lib/api';

export const AutoImportBanner = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await GmailApi.bannerStatus();
        if (!cancelled) setData(res);
      } catch (_) {
        // Banner is best-effort — failing silently is fine.
        if (!cancelled) setData(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data || !data.show) return null;

  const applied = data.auto_applied || 0;
  const drafts = data.drafts_created || 0;
  const total = applied + drafts;
  const hasApplied = applied > 0;
  const hasDrafts = drafts > 0;

  const dismiss = async () => {
    setDismissing(true);
    try {
      await GmailApi.bannerDismiss();
      setData(null);
    } catch (_) {
      setDismissing(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-accent/30 bg-accent/[0.06] px-4 py-3 sm:px-5 sm:py-4 flex items-start sm:items-center gap-3 flex-col sm:flex-row"
      data-testid="dashboard-auto-import-banner"
    >
      <div className="shrink-0 h-9 w-9 rounded-lg bg-accent/15 text-accent grid place-items-center">
        <MailCheck className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
          <Sparkles className="h-3.5 w-3.5 text-accent shrink-0" />
          <span>
            {total} {total === 1 ? 'transaction' : 'transactions'} auto-imported overnight
          </span>
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {hasApplied && hasDrafts
            ? `${applied} added to your wallet, ${drafts} need a quick review.`
            : hasApplied
              ? `${applied} added to your wallet from your bank emails.`
              : `${drafts} drafts ready for review from your bank emails.`}
        </div>
      </div>
      <div className="flex items-center gap-2 self-stretch sm:self-auto">
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1 border-accent/40 bg-accent/10 hover:bg-accent/20 text-accent"
          onClick={() => navigate(hasDrafts ? '/sync/gmail' : '/transactions')}
          data-testid="dashboard-auto-import-review"
        >
          Review <ChevronRight className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          onClick={dismiss}
          disabled={dismissing}
          aria-label="Dismiss"
          data-testid="dashboard-auto-import-dismiss"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default AutoImportBanner;
